import React, { forwardRef, useCallback } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { IngredientCard } from '@sg/garnish';

import { useDietaryRestrictions } from '@order/components';
import { useLocalizationContext } from '@order/Localization';

import type { IngredientModificationWithQuantity } from '../../types';
import {
  useIngredientModificationA11yLabels,
  useIngredientModificationCardTestID,
  useIngredientModificationQty,
} from './IngredientModificationCard.hooks';

export const IngredientModificationCard = forwardRef<
  View,
  IngredientModificationCardProps
>((props, ref) => {
  const {
    ingredientModification,
    testIdPrefix,
    isSelected,
    isUnavailable,
    accessibilityLabel,
    style,
    onPress,
    onDecreaseQuantity,
  } = props;
  const { ingredient } = ingredientModification;
  const { name: ingredientName, asset, dietaryProperties } = ingredient;
  const getRestrictionNotice = useDietaryRestrictions();

  const { t } = useLocalizationContext();

  // ────────────────────────────────────────────────────────────────────

  const ingredientAssetUrl = asset.url;
  const ingredientQty = useIngredientModificationQty(ingredientModification);
  const testID = useIngredientModificationCardTestID(
    ingredientModification,
    testIdPrefix,
  );

  // ─── HELPERS ────────────────────────────────────────────────────────

  const handleOnPress = useCallback(() => {
    return onPress?.(ingredientModification);
  }, [ingredientModification, onPress]);

  const handleOnDecreaseQuantity = useCallback(() => {
    return onDecreaseQuantity?.(ingredientModification);
  }, [ingredientModification, onDecreaseQuantity]);

  // ─── A11Y ───────────────────────────────────────────────────────────

  const { increaseQtyA11yLabel, decreaseQtyA11yLabel } =
    useIngredientModificationA11yLabels(
      ingredientModification,
      ingredientQty,
      accessibilityLabel,
    );

  // ────────────────────────────────────────────────────────────────────

  const wrapperStyle = [styles.wrapper, style];

  return (
    <View style={wrapperStyle}>
      <IngredientCard
        testID={testID}
        ref={ref}
        title={ingredientName}
        imgUrl={ingredientAssetUrl}
        isSelected={isSelected}
        isUnavailable={isUnavailable}
        quantity={ingredientQty}
        unavailableNotice={t('general.unavailable')}
        onPress={onPress ? handleOnPress : undefined}
        onDecreaseQuantity={
          onDecreaseQuantity ? handleOnDecreaseQuantity : undefined
        }
        accessibilityLabel={increaseQtyA11yLabel}
        decreaseQuantityAccessibilityLabel={decreaseQtyA11yLabel}
        restrictionsNotice={getRestrictionNotice(dietaryProperties)}
      />
    </View>
  );
});

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  wrapper: {
    flexGrow: 1,
  },
});

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type IngredientModificationCardProps = {
  readonly ingredientModification: IngredientModificationWithQuantity;
  readonly testIdPrefix?: string;
  readonly isUnavailable?: boolean;
  readonly isSelected?: boolean;
  readonly accessibilityLabel?: ViewProps['accessibilityLabel'];
  readonly onPress?: (
    ingredientModification: IngredientModificationWithQuantity,
  ) => void;
  readonly onDecreaseQuantity?: (
    ingredientModification: IngredientModificationWithQuantity,
  ) => void;
  readonly style?: ViewProps['style'];
};
